import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { FsSignedUrlService } from '@shared/services/api/fs-signed-url.service';
import { GenerateSignedUrlResponse } from '@shared/entities/generateSignedUrlResponse';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-storage-attachment-input',
  templateUrl: './storage-attachment-input.component.html',
  styleUrls: ['./storage-attachment-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StorageAttachmentInputComponent),
      multi: true
    }
  ]
})
export class StorageAttachmentInputComponent implements ControlValueAccessor, OnDestroy {
  @Input() accept = '*';
  @Input() labelKey!: string;
  @Input() showTitle = false;
  @Input() titleKey!: string;
  @Input() infoPopover?: string;
  @Input() hideLabel = false;
  @Input() disabled = false;
  @Input() autoUpload = true; // Se true, carica automaticamente il file dopo la selezione

  @Output() fileSelected = new EventEmitter<File>();
  @Output() uploadStarted = new EventEmitter<void>();
  @Output() uploadCompleted = new EventEmitter<string>(); // Emette il path del file caricato
  @Output() uploadFailed = new EventEmitter<string>(); // Emette il messaggio di errore

  selectedFile?: File;
  uploadProgress = 0;
  uploadStatus?: string;
  error?: string;
  signedUrlResponse?: GenerateSignedUrlResponse | null;
  uploadedFilePath?: string;

  readonly inputId = 'storage-attachment-' + Math.random().toString(36).slice(2, 9);

  private onChange: (v: any) => void = () => {
    /* noop */
  };
  private onTouched: () => void = () => {
    /* noop */
  };
  private destroy$ = new Subject<void>();

  constructor(
    private fsSignedUrlService: FsSignedUrlService,
    private http: HttpClient
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input || !input.files || !input.files.length) return;

    this.selectedFile = input.files[0];
    this.uploadProgress = 0;
    this.uploadStatus = undefined;
    this.error = undefined;
    this.signedUrlResponse = null;
    this.uploadedFilePath = undefined;

    this.fileSelected.emit(this.selectedFile);
    this.onTouched();

    if (this.autoUpload) {
      this.startUpload();
    }

    // Reset input value per permettere di selezionare lo stesso file
    input.value = '';
  }

  startUpload() {
    if (!this.selectedFile) {
      this.error = 'Nessun file selezionato';
      return;
    }

    // Step 1: Genera Signed URL
    this.generateSignedUrl();
  }

  private generateSignedUrl() {
    if (!this.selectedFile) return;

    this.error = undefined;
    this.uploadStatus = 'Generazione URL...';

    const payload: any = {
      fileName: this.selectedFile.name
    };

    if (this.selectedFile.type) {
      payload.contentType = this.selectedFile.type;
    }

    this.fsSignedUrlService
      .generateSignedUrl(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.signedUrlResponse = res;
          // Step 2: Upload del file
          this.uploadToSignedUrl();
        },
        error: (err) => {
          this.error = typeof err === 'string' ? err : JSON.stringify(err);
          this.uploadStatus = 'Errore nella generazione URL';
          this.uploadFailed.emit(this.error);
        }
      });
  }

  private uploadToSignedUrl() {
    if (!this.signedUrlResponse || !this.signedUrlResponse.url || !this.selectedFile) {
      this.error = 'URL non disponibile';
      return;
    }

    this.error = undefined;
    this.uploadProgress = 0;
    this.uploadStatus = 'Caricamento in corso...';
    this.uploadStarted.emit();

    const headers = new HttpHeaders({});

    this.http
      .put(this.signedUrlResponse.url, this.selectedFile, {
        headers,
        reportProgress: true,
        observe: 'events'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round((100 * event.loaded) / event.total);
          } else if (event.type === HttpEventType.Response) {
            this.uploadProgress = 100;
            this.uploadStatus = 'Caricamento completato';

            // Step 3: Estrai il path del file dall'URL del signed URL
            if (this.signedUrlResponse?.url) {
              this.uploadedFilePath = this.extractPathFromSignedUrl(this.signedUrlResponse.url);
              // Step 4: Aggiorna il form control con il path del file
              this.onChange(this.uploadedFilePath);
              this.uploadCompleted.emit(this.uploadedFilePath);
            }
          }
        },
        error: (err) => {
          this.uploadStatus = 'Caricamento fallito';
          try {
            this.error = typeof err === 'string' ? err : JSON.stringify(err);
          } catch {
            this.error = 'Upload fallito (vedi log per dettagli)';
          }
          this.uploadFailed.emit(this.error);
        }
      });
  }

  removeFile() {
    this.selectedFile = undefined;
    this.uploadProgress = 0;
    this.uploadStatus = undefined;
    this.error = undefined;
    this.signedUrlResponse = null;
    this.uploadedFilePath = undefined;
    this.onChange(null);
  }

  get isUploading(): boolean {
    return this.uploadProgress > 0 && this.uploadProgress < 100;
  }

  get isUploadComplete(): boolean {
    return this.uploadProgress === 100 && !!this.uploadedFilePath;
  }

  private extractPathFromSignedUrl(signedUrl: string): string {
    try {
      // L'URL di Google Cloud Storage ha il formato:
      // https://storage.googleapis.com/bucket-name/path/to/file?query-params
      const url = new URL(signedUrl);
      const pathname = url.pathname;

      // Rimuovi il primo slash e il nome del bucket
      const parts = pathname.split('/').filter((p) => p);
      if (parts.length > 1) {
        // Rimuovi il bucket name (primo elemento) e restituisci il resto
        return parts.slice(1).join('/');
      }

      // Fallback: usa il nome del file selezionato
      return this.selectedFile?.name || '';
    } catch {
      // In caso di errore, usa il nome del file
      return this.selectedFile?.name || '';
    }
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    // Il valore scritto sarà il path del file caricato
    this.uploadedFilePath = value;
    if (!value) {
      this.selectedFile = undefined;
      this.uploadProgress = 0;
      this.uploadStatus = undefined;
      this.error = undefined;
      this.signedUrlResponse = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
